"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import Image from "next/image"

interface AIModel {
  name: string
  image: string
  description: string
}

const aiModels: AIModel[] = [
  {
    name: "OpenAI",
    image: "/images/chat-gpt-50.png",
    description: "GPT-4 & Turbo",
  },
  {
    name: "Google Gemini",
    image: "/images/gemini-ai-48.png",
    description: "Gemini Pro",
  },
  {
    name: "Anthropic Claude",
    image: "/images/claude-ai-48.png",
    description: "Claude 3",
  },
  {
    name: "Grok",
    image: "/images/grok-128.png",
    description: "Fast Inference",
  },
  {
    name: "Kimi Ai",
    image: "/images/kimi-ai.jpg",
    description: "Kimi 2.5",
  },
  {
    name: "Midjourney",
    image: "/images/midjourney-48.png",
    description: "Image Generation",
  },
  {
    name: "Meta Llama",
    image: "/images/meta-icon.png",
    description: "Llama 3",
  },
  {
    name: "Together AI",
    image: "/images/together-ai-icon.png",
    description: "Multiple Models",
  },
]

type CarouselTheme = "light" | "dark"
type CarouselVariant = "ring" | "coverflow"

interface AIModelsCarouselProps {
  theme?: CarouselTheme
  largeText?: boolean
  variant?: CarouselVariant
}

function ModelCard({
  model,
  isDark,
  largeText,
  showDescription,
  compact,
}: {
  model: AIModel
  isDark: boolean
  largeText: boolean
  showDescription: boolean
  compact?: boolean
}) {
  const logoSize = compact ? "w-20 h-20" : "w-24 h-24"
  const imageSize = compact ? 60 : 72

  return (
    <div
      className={`group flex flex-col items-center justify-center gap-3 rounded-2xl border-2 shadow-lg transition-all duration-300 ${
        compact ? "p-4" : "p-5"
      } ${
        isDark
          ? "bg-landing-surface-2 border-landing-border hover:border-landing-accent"
          : "bg-white border-gray-200/60 hover:border-[#01ADEF]"
      }`}
    >
      <div
        className={`${logoSize} rounded-xl flex items-center justify-center shadow-md transform group-hover:scale-110 transition-transform duration-300 overflow-hidden border ${
          isDark ? "bg-landing-surface-3 border-landing-border" : "bg-white border-gray-200"
        }`}
      >
        <Image
          src={model.image}
          alt={model.name}
          width={imageSize}
          height={imageSize}
          className="object-contain p-2"
        />
      </div>

      <div
        className={`${largeText ? "text-lg" : "text-base"} font-bold text-center transition-colors duration-300 ${
          isDark
            ? "text-landing-text group-hover:text-landing-accent"
            : "text-gray-900 group-hover:text-[#01ADEF]"
        }`}
      >
        {model.name}
      </div>

      {showDescription && (
        <div
          className={`${largeText ? "text-sm" : "text-xs"} text-center font-medium transition-colors duration-300 ${
            isDark
              ? "text-landing-muted group-hover:text-landing-accent"
              : "text-gray-500 group-hover:text-[#0964b3]"
          }`}
        >
          {model.description}
        </div>
      )}
    </div>
  )
}

function RingCarousel({ theme, largeText }: { theme: CarouselTheme; largeText: boolean }) {
  const isDark = theme === "dark"
  const [rotation, setRotation] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const animationRef = useRef<number | undefined>(undefined)
  const containerRef = useRef<HTMLDivElement>(null)

  const totalModels = aiModels.length
  const angleStep = 360 / totalModels
  const [radius, setRadius] = useState(280)

  useEffect(() => {
    const updateRadius = () => {
      setRadius(window.innerWidth < 768 ? 180 : 280)
    }
    updateRadius()
    window.addEventListener("resize", updateRadius)
    return () => window.removeEventListener("resize", updateRadius)
  }, [])

  useEffect(() => {
    const animate = () => {
      if (!isPaused) {
        setRotation((prev) => prev + 0.21)
      }
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPaused])

  return (
    <div className="relative w-full flex flex-col items-center justify-center">
      <div
        ref={containerRef}
        className="relative w-full h-[350px] md:h-[400px]"
        style={{
          perspective: "1200px",
          perspectiveOrigin: "50% 50%",
        }}
      >
        <div
          className="absolute top-1/2 left-1/2"
          style={{
            transform: `translate(-50%, -50%) rotateY(${rotation}deg)`,
            transformStyle: "preserve-3d",
            transition: isPaused ? "transform 0.5s ease-out" : "none",
            width: "100%",
            height: "100%",
          }}
        >
          {aiModels.map((model, index) => {
            const angle = (index * angleStep - 90) * (Math.PI / 180)
            const x = Math.cos(angle) * radius
            const z = Math.sin(angle) * radius
            const rotateY = -index * angleStep
            const normalizedZ = (z + radius) / (radius * 2)
            const scale = 0.6 + normalizedZ * 0.4

            return (
              <div
                key={index}
                className="absolute"
                style={{
                  transform: `translateX(${x}px) translateZ(${z}px) rotateY(${rotateY}deg) scale(${scale})`,
                  transformStyle: "preserve-3d",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  opacity: 1,
                  width: "210px",
                  left: "50%",
                  top: "50%",
                  marginLeft: "-105px",
                  marginTop: "-105px",
                  transition: isPaused ? "transform 0.3s" : "none",
                }}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <ModelCard
                  model={model}
                  isDark={isDark}
                  largeText={largeText}
                  showDescription
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function CoverflowCarousel({ theme, largeText }: { theme: CarouselTheme; largeText: boolean }) {
  const isDark = theme === "dark"
  const totalModels = aiModels.length
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const touchStartX = useRef(0)
  const touchDeltaX = useRef(0)

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % totalModels)
  }, [totalModels])

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + totalModels) % totalModels)
  }, [totalModels])

  useEffect(() => {
    const mqMobile = window.matchMedia("(max-width: 767px)")
    const mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)")

    const updateMobile = () => setIsMobile(mqMobile.matches)
    const updateMotion = () => setReduceMotion(mqMotion.matches)

    updateMobile()
    updateMotion()
    mqMobile.addEventListener("change", updateMobile)
    mqMotion.addEventListener("change", updateMotion)

    return () => {
      mqMobile.removeEventListener("change", updateMobile)
      mqMotion.removeEventListener("change", updateMotion)
    }
  }, [])

  useEffect(() => {
    if (isPaused || reduceMotion) return

    const interval = window.setInterval(goNext, 3500)
    return () => window.clearInterval(interval)
  }, [isPaused, reduceMotion, goNext])

  const getRelativeOffset = (index: number) => {
    let diff = index - activeIndex
    const half = Math.floor(totalModels / 2)
    if (diff > half) diff -= totalModels
    if (diff < -half) diff += totalModels
    return diff
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
    setIsPaused(true)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
  }

  const handleTouchEnd = () => {
    const delta = touchDeltaX.current
    if (delta > 50) {
      goPrev()
    } else if (delta < -50) {
      goNext()
    }
    setIsPaused(false)
  }

  const cardWidth = isMobile ? 200 : 220
  const spread = isMobile ? 0 : 260

  return (
    <div className="relative w-full flex flex-col items-center justify-center">
      <div
        className="relative w-full h-[300px] sm:h-[340px] md:h-[380px] touch-pan-y"
        style={{
          perspective: "1400px",
          perspectiveOrigin: "50% 42%",
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          {aiModels.map((model, index) => {
            const offset = getRelativeOffset(index)

            if (isMobile && offset !== 0) return null
            if (!isMobile && Math.abs(offset) > 1) return null

            const isCenter = offset === 0
            const translateX = offset * spread
            const rotateY = offset * -42
            const scale = isCenter ? 1 : 0.82
            const opacity = isCenter ? 1 : 0.55
            const zIndex = 20 - Math.abs(offset)

            return (
              <div
                key={model.name}
                className="absolute left-1/2 top-1/2 will-change-transform"
                style={{
                  width: `${cardWidth}px`,
                  marginLeft: `-${cardWidth / 2}px`,
                  marginTop: isMobile ? "-110px" : "-120px",
                  transform: `translateX(${translateX}px) rotateY(${rotateY}deg) scale(${scale})`,
                  opacity,
                  zIndex,
                  transition: "transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s ease",
                  transformStyle: "preserve-3d",
                  pointerEvents: isCenter ? "auto" : "none",
                }}
              >
                <ModelCard
                  model={model}
                  isDark={isDark}
                  largeText={largeText}
                  showDescription={false}
                  compact={isMobile}
                />
              </div>
            )
          })}
        </div>
      </div>

      {isMobile && (
        <div className="flex items-center justify-center gap-2 mt-2" aria-hidden>
          {aiModels.map((model, index) => (
            <button
              key={model.name}
              type="button"
              aria-label={`Show ${model.name}`}
              onClick={() => setActiveIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? "w-6 bg-landing-accent"
                  : "w-2 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function AIModelsCarousel({
  theme = "light",
  largeText = false,
  variant = "ring",
}: AIModelsCarouselProps) {
  if (variant === "coverflow") {
    return <CoverflowCarousel theme={theme} largeText={largeText} />
  }

  return <RingCarousel theme={theme} largeText={largeText} />
}
